import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { 
  Save, 
  Add, 
  Delete, 
  PhotoCamera,
  LocationOn,
  Schedule,
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Mock data and helper functions
const categories = [
  'Electricistas',
  'Plomeros',
  'Carpinteros',
  'Pintores',
  'Jardineros'
];

const locations = [
  'Ciudad de México',
  'Guadalajara',
  'Monterrey',
  'Puebla',
  'Querétaro',
  'Tijuana',
  'Mérida',
  'Cancún',
  'Puerto Vallarta',
  'Acapulco'
];

const daysOfWeek = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo'
];

// Add a common input style that will be reused for all form fields
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': {
      backgroundColor: '#fafafa',
      '& fieldset': {
        borderColor: '#bdbdbd',
      },
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(255, 229, 0, 0.2)',
      '& fieldset': {
        borderColor: '#FFD700',
        borderWidth: '1px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#2C3E50',
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d0d0d0',
  },
};

// Additional global styles to add to improve all form fields
const theme = {
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '16px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 229, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 229, 0, 0.2)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filled': {
            backgroundColor: '#FFE500',
          },
        },
      },
    },
  },
};

// Define the enhanced paper style to be reused for all sections
const paperStyle = {
  p: 3, 
  borderRadius: '16px',
  border: '1px solid #e0e0e0',
  position: 'relative',
  mb: 3,
  backgroundColor: '#fcfcfc',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  }
};

// Define the editable label style to be reused for all sections
const editableLabelStyle = {
  display: 'inline-block',
  mb: 2,
  color: '#666',
  backgroundColor: 'rgba(255,229,0,0.1)',
  px: 1,
  py: 0.5,
  borderRadius: 1,
  fontWeight: 500,
};

// Add a label style for form fields to make them more prominent
const labelStyle = {
  color: '#2C3E50',
  fontWeight: 500,
  fontSize: '0.9rem',
};

// Define a style for form field focus indicator
const focusIndicatorStyle = {
  '&:focus-within': {
    '& .MuiInputLabel-root': {
      color: '#FFD700',
    },
  },
};

// Combine the styles for even better form field interactions
const enhancedInputStyles = {
  ...inputStyles,
  ...focusIndicatorStyle,
  '& .MuiInputLabel-root': {
    ...labelStyle,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: '#f8f8f8',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#c0c0c0',
    },
  },
  '& .MuiInputBase-input': {
    color: '#000000',
    fontSize: '1rem',
  },
};

// Define a specific style for the multiline text field
const textareaStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#c0c0c0',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a0a0a0',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFD700',
    borderWidth: '1px',
    boxShadow: '0 0 0 3px rgba(255, 229, 0, 0.2)',
  },
};

// Define a simple style for the textarea
const textareaStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #c0c0c0',
  minHeight: '120px',
  padding: '12px',
  width: '100%',
  fontFamily: 'inherit',
  fontSize: '1rem',
  color: '#000',
  resize: 'vertical',
  '&:hover': {
    borderColor: '#a0a0a0',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#FFD700',
    boxShadow: '0 0 0 3px rgba(255, 229, 0, 0.2)',
  },
};

const UserProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL parameters if available
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Use ID from URL if available, otherwise load first provider
  const [providerId, setProviderId] = useState(id || null);
  
  // Form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    category: '',
    description: '',
    hourlyRate: '',
    image: '',
    workingDays: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    workingHours: {
      start: '09:00',
      end: '18:00',
    },
    services: []
  });
  
  // Service form
  const [serviceForm, setServiceForm] = useState({
    name: '',
    price: '',
    description: ''
  });
  
  // Validation errors
  const [formErrors, setFormErrors] = useState({});
  
  useEffect(() => {
    const loadProvider = async () => {
      try {
        if (providerId) {
          // If we already have an ID (from URL params), load that specific provider
          const providerDoc = await getDoc(doc(db, 'providers', providerId));
          if (providerDoc.exists()) {
            const providerData = providerDoc.data();
            
            console.log("Loaded provider data:", providerData);
            console.log("Provider availability:", providerData.availability);
            
            // Process workingDays from availability if it exists
            let workingDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']; // Default
            
            if (providerData.availability) {
              // If availability has workingDays array, use it directly
              if (providerData.availability.workingDays && Array.isArray(providerData.availability.workingDays)) {
                workingDays = providerData.availability.workingDays;
                console.log("Using workingDays array from availability:", workingDays);
              } 
              // Otherwise, convert from boolean format if present
              else if (typeof providerData.availability.monday === 'boolean') {
                workingDays = [];
                if (providerData.availability.monday) workingDays.push('Lunes');
                if (providerData.availability.tuesday) workingDays.push('Martes');
                if (providerData.availability.wednesday) workingDays.push('Miércoles');
                if (providerData.availability.thursday) workingDays.push('Jueves');
                if (providerData.availability.friday) workingDays.push('Viernes');
                if (providerData.availability.saturday) workingDays.push('Sábado');
                if (providerData.availability.sunday) workingDays.push('Domingo');
                console.log("Converted boolean availability to workingDays:", workingDays);
              }
            }
            
            // Set working hours from availability if available
            const workingHours = providerData.availability?.workingHours || { start: '09:00', end: '18:00' };
            
            setProfileForm({
              name: providerData.name || '',
              email: providerData.email || '',
              phone: providerData.phone || '',
              location: providerData.location || '',
              category: providerData.category || '',
              description: providerData.description || '',
              hourlyRate: providerData.hourlyRate || '',
              image: providerData.image || '',
              workingDays, // Use the processed workingDays
              workingHours, // Use the processed workingHours
              services: providerData.services || []
            });
          } else {
            setError('No se encontró el proveedor especificado.');
          }
        } else {
          // If no ID provided, load the first provider (for demo purposes)
          const providersSnapshot = await getDocs(collection(db, 'providers'));
          if (!providersSnapshot.empty) {
            const firstProvider = providersSnapshot.docs[0];
            setProviderId(firstProvider.id);
            
            const providerData = firstProvider.data();
            console.log("Loaded first provider data:", providerData);
            console.log("First provider availability:", providerData.availability);
            
            // Process workingDays from availability if it exists
            let workingDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']; // Default
            
            if (providerData.availability) {
              // If availability has workingDays array, use it directly
              if (providerData.availability.workingDays && Array.isArray(providerData.availability.workingDays)) {
                workingDays = providerData.availability.workingDays;
                console.log("Using workingDays array from availability:", workingDays);
              } 
              // Otherwise, convert from boolean format if present
              else if (typeof providerData.availability.monday === 'boolean') {
                workingDays = [];
                if (providerData.availability.monday) workingDays.push('Lunes');
                if (providerData.availability.tuesday) workingDays.push('Martes');
                if (providerData.availability.wednesday) workingDays.push('Miércoles');
                if (providerData.availability.thursday) workingDays.push('Jueves');
                if (providerData.availability.friday) workingDays.push('Viernes');
                if (providerData.availability.saturday) workingDays.push('Sábado');
                if (providerData.availability.sunday) workingDays.push('Domingo');
                console.log("Converted boolean availability to workingDays:", workingDays);
              }
            }
            
            // Set working hours from availability if available
            const workingHours = providerData.availability?.workingHours || { start: '09:00', end: '18:00' };
            
            setProfileForm({
              name: providerData.name || '',
              email: providerData.email || '',
              phone: providerData.phone || '',
              location: providerData.location || '',
              category: providerData.category || '',
              description: providerData.description || '',
              hourlyRate: providerData.hourlyRate || '',
              image: providerData.image || '',
              workingDays, // Use the processed workingDays
              workingHours, // Use the processed workingHours
              services: providerData.services || []
            });
          } else {
            setError('No se encontraron proveedores en la base de datos.');
          }
        }
      } catch (error) {
        console.error('Error loading provider:', error);
        setError('Error al cargar los datos del proveedor.');
      } finally {
        setLoading(false);
      }
    };
    
    loadProvider();
  }, [providerId, id]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      [name]: value
    });
    
    // Clear validation error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  const handleWorkingHoursChange = (e) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      workingHours: {
        ...profileForm.workingHours,
        [name]: value
      }
    });
  };
  
  const handleWorkingDaysChange = (e) => {
    setProfileForm({
      ...profileForm,
      workingDays: e.target.value
    });
  };
  
  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm({
      ...serviceForm,
      [name]: value
    });
  };
  
  const addService = () => {
    // Validate service form
    const errors = {};
    if (!serviceForm.name.trim()) errors.serviceName = 'El nombre del servicio es requerido';
    if (!serviceForm.price) errors.servicePrice = 'El precio es requerido';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors({
        ...formErrors,
        ...errors
      });
      return;
    }
    
    const newService = {
      name: serviceForm.name,
      price: parseFloat(serviceForm.price),
      description: serviceForm.description
    };
    
    setProfileForm({
      ...profileForm,
      services: [...profileForm.services, newService]
    });
    
    setServiceForm({
      name: '',
      price: '',
      description: ''
    });
    
    // Clear service form errors
    const updatedErrors = { ...formErrors };
    delete updatedErrors.serviceName;
    delete updatedErrors.servicePrice;
    setFormErrors(updatedErrors);
  };
  
  const removeService = (index) => {
    const updatedServices = [...profileForm.services];
    updatedServices.splice(index, 1);
    setProfileForm({
      ...profileForm,
      services: updatedServices
    });
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!profileForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!profileForm.email.trim()) errors.email = 'El email es requerido';
    if (!profileForm.phone.trim()) errors.phone = 'El teléfono es requerido';
    if (!profileForm.location) errors.location = 'La ubicación es requerida';
    if (!profileForm.category) errors.category = 'La categoría es requerida';
    if (!profileForm.description.trim()) errors.description = 'La descripción es requerida';
    if (!profileForm.hourlyRate) errors.hourlyRate = 'La tarifa por hora es requerida';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!providerId) {
      setError('No se ha cargado ningún proveedor para actualizar.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      // Prepare data for update - format availability to be compatible with both pages
      const formattedData = {
        ...profileForm,
        // Save both workingDays array and boolean flags for backward compatibility
        availability: {
          workingDays: profileForm.workingDays,
          workingHours: profileForm.workingHours,
          // Add boolean flags for each day to maintain compatibility with ProviderProfile.js
          monday: profileForm.workingDays.includes('Lunes'),
          tuesday: profileForm.workingDays.includes('Martes'),
          wednesday: profileForm.workingDays.includes('Miércoles'),
          thursday: profileForm.workingDays.includes('Jueves'),
          friday: profileForm.workingDays.includes('Viernes'),
          saturday: profileForm.workingDays.includes('Sábado'),
          sunday: profileForm.workingDays.includes('Domingo')
        },
        updatedAt: new Date()
      };
      
      console.log("Saving formatted provider data:", formattedData);
      
      // Update provider data in Firestore
      await updateDoc(doc(db, 'providers', providerId), formattedData);
      
      // Verify the data was updated correctly
      const updatedDoc = await getDoc(doc(db, 'providers', providerId));
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        console.log("Updated provider data:", updatedData);
        console.log("Updated availability:", updatedData.availability);
        console.log("Working days saved:", updatedData.availability.workingDays);
        console.log("Boolean flags saved:", {
          monday: updatedData.availability.monday,
          tuesday: updatedData.availability.tuesday,
          wednesday: updatedData.availability.wednesday,
          thursday: updatedData.availability.thursday,
          friday: updatedData.availability.friday,
          saturday: updatedData.availability.saturday,
          sunday: updatedData.availability.sunday
        });
      }
      
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating provider:', error);
      setError('Error al actualizar los datos. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Add transitions to all controls for better interactivity
  const buttonTransition = {
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
  };
  
  return (
    <Box sx={{ pt: 12, pb: 8, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ¡Perfil actualizado con éxito!
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Provider CTA */}
        <Paper 
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 3,
            borderRadius: { xs: '12px', md: '16px' },
            backgroundColor: '#FFFDE7',
            border: '1px solid #FFE082',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: { xs: '3px', md: '4px' },
              height: '100%',
              backgroundColor: '#FFD700'
            }} 
          />
          
          <Box sx={{ 
            flex: 1, 
            pr: { xs: 0, md: 4 }, 
            mb: { xs: 2.5, md: 0 }, 
            width: '100%',
            pl: { xs: 1.5, sm: 1 }
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#2C3E50',
                fontFamily: 'Ubuntu',
                mb: { xs: 0.75, md: 1 },
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                lineHeight: 1.3
              }}
            >
              ¡Conviértete en Proveedor!
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: { xs: 1, md: 1 }, 
                fontSize: { xs: '0.813rem', sm: '0.875rem', md: '1rem' },
                lineHeight: { xs: 1.4, md: 1.6 }
              }}
            >
              Comparte tus habilidades, establece tus precios y empieza a recibir trabajos. Únete a nuestra red de profesionales.
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/provider-application')}
            sx={{ 
              bgcolor: '#FFD700',
              color: 'black',
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              fontWeight: 600,
              fontSize: { xs: '0.875rem', md: '1rem' },
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              alignSelf: { xs: 'stretch', md: 'auto' },
              width: { xs: '100%', md: 'auto' },
              minHeight: { xs: '44px', md: 'auto' },
              '&:hover': {
                bgcolor: '#FFC107',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.2s ease'
            }}
          >
            Registrarme como Proveedor
          </Button>
        </Paper>
        
        <Grid container spacing={3}>
          {/* Profile Basic Info */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={paperStyle}
            >
              <Box 
                sx={{ 
                  height: '4px', 
                  bgcolor: '#FFE500',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px'
                }} 
              />
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700, 
                  color: '#2C3E50',
                  fontFamily: 'Ubuntu',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 3,
                    backgroundColor: '#FFE500',
                  }
                }}
              >
                Información Básica
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    <Avatar
                      src={profileForm.image}
                      alt={profileForm.name}
                      sx={{ 
                        width: 150, 
                        height: 150, 
                        mx: 'auto',
                        border: '4px solid #fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        cursor: 'pointer',
                      }}
                    />
                    <Box 
                      sx={{ 
                        mt: 2, 
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <IconButton 
                        color="primary" 
                        aria-label="upload picture"
                        component="label"
                        sx={{ 
                          bgcolor: '#f0f0f0',
                          '&:hover': {
                            bgcolor: '#FFE500',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <input hidden accept="image/*" type="file" />
                        <PhotoCamera />
                      </IconButton>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Cambiar foto
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sube una foto profesional para tu perfil
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Nombre Completo
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        placeholder="Ingresa tu nombre completo"
                        name="name"
                        value={profileForm.name}
                        onChange={handleInputChange}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        sx={enhancedInputStyles}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Email
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        placeholder="Ingresa tu correo electrónico"
                        name="email"
                        value={profileForm.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        sx={enhancedInputStyles}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Teléfono
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        placeholder="Ingresa tu número telefónico"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleInputChange}
                        error={!!formErrors.phone}
                        helperText={formErrors.phone}
                        sx={enhancedInputStyles}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Ubicación
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <FormControl 
                        fullWidth 
                        error={!!formErrors.location}
                        sx={enhancedInputStyles}
                      >
                        <Select
                          name="location"
                          value={profileForm.location}
                          displayEmpty
                          renderValue={selected => selected ? selected : "Selecciona tu ubicación"}
                          onChange={handleInputChange}
                          startAdornment={
                            <InputAdornment position="start">
                              <LocationOn fontSize="small" />
                            </InputAdornment>
                          }
                        >
                          {locations.map((location) => (
                            <MenuItem key={location} value={location}>
                              {location}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.location && (
                          <FormHelperText>{formErrors.location}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Categoría de Servicio
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <FormControl 
                        fullWidth 
                        error={!!formErrors.category}
                        sx={enhancedInputStyles}
                      >
                        <Select
                          name="category"
                          value={profileForm.category}
                          displayEmpty
                          renderValue={selected => selected ? selected : "Selecciona tu categoría"}
                          onChange={handleInputChange}
                        >
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.category && (
                          <FormHelperText>{formErrors.category}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Descripción Profesional
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <textarea
                          style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #c0c0c0',
                            minHeight: '120px',
                            padding: '12px',
                            width: '100%',
                            fontFamily: 'inherit',
                            fontSize: '1rem',
                            color: '#000',
                            resize: 'vertical',
                          }}
                          placeholder="Describe tu experiencia, especialidades y cualquier información relevante sobre tus servicios."
                          name="description"
                          value={profileForm.description}
                          onChange={handleInputChange}
                        ></textarea>
                        {formErrors.description && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 1 }}>
                            {formErrors.description}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C3E50' }}>
                          Tarifa por Hora (MXN)
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        type="number"
                        placeholder="Ingresa tu tarifa por hora"
                        name="hourlyRate"
                        value={profileForm.hourlyRate}
                        onChange={handleInputChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={!!formErrors.hourlyRate}
                        helperText={formErrors.hourlyRate}
                        sx={enhancedInputStyles}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Availability */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={paperStyle}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700, 
                  color: '#2C3E50',
                  fontFamily: 'Ubuntu',
                }}
              >
                Disponibilidad
              </Typography>
              
              <Typography variant="caption" sx={editableLabelStyle}>
                Editar disponibilidad
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth
                    sx={enhancedInputStyles}
                  >
                    <InputLabel>Días Laborables</InputLabel>
                    <Select
                      multiple
                      value={profileForm.workingDays}
                      onChange={handleWorkingDaysChange}
                      label="Días Laborables"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" sx={{ bgcolor: 'rgba(255, 229, 0, 0.1)' }} />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {daysOfWeek.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Hora de Inicio"
                    name="start"
                    type="time"
                    value={profileForm.workingHours.start}
                    onChange={handleWorkingHoursChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Schedule fontSize="small" sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={enhancedInputStyles}
                  />
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Hora de Fin"
                    name="end"
                    type="time"
                    value={profileForm.workingHours.end}
                    onChange={handleWorkingHoursChange}
                    InputLabelProps={{ shrink: true }}
                    sx={enhancedInputStyles}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Services */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={paperStyle}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700, 
                  color: '#2C3E50',
                  fontFamily: 'Ubuntu',
                }}
              >
                Servicios Ofrecidos
              </Typography>
              
              <Typography variant="caption" sx={editableLabelStyle}>
                Editar información
              </Typography>
              
              <Box sx={{ 
                mb: 4, 
                p: 2, 
                borderRadius: '8px', 
                border: '1px dashed #d0d0d0',
                backgroundColor: '#fff',
                transition: 'all 0.2s',
                '&:focus-within': {
                  borderColor: '#FFD700',
                  boxShadow: '0 0 0 3px rgba(255, 229, 0, 0.2)',
                  borderStyle: 'solid',
                },
                '&:hover': {
                  borderColor: '#bdbdbd',
                },
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Agrega los servicios específicos que ofreces junto con sus precios
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Nombre del servicio"
                      name="name"
                      value={serviceForm.name}
                      onChange={handleServiceInputChange}
                      error={!!formErrors.serviceName}
                      helperText={formErrors.serviceName}
                      sx={{
                        ...enhancedInputStyles,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      placeholder="Precio del servicio"
                      name="price"
                      value={serviceForm.price}
                      onChange={handleServiceInputChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      error={!!formErrors.servicePrice}
                      helperText={formErrors.servicePrice}
                      sx={{
                        ...enhancedInputStyles,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Descripción del servicio (opcional)"
                      name="description"
                      value={serviceForm.description}
                      onChange={handleServiceInputChange}
                      sx={{
                        ...enhancedInputStyles,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={addService}
                      sx={{ 
                        minWidth: '40px', 
                        width: '40px', 
                        height: '40px',
                        p: 0,
                        bgcolor: '#FFE500',
                        color: 'black',
                        ...buttonTransition
                      }}
                    >
                      <Add />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              {profileForm.services.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f9f9f9', borderRadius: '8px' }}>
                  <Typography variant="body1" color="text.secondary">
                    No has agregado ningún servicio aún.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {profileForm.services.map((service, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#fff',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#FFE500',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {service.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${service.price} MXN
                          </Typography>
                          {service.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {service.description}
                            </Typography>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => removeService(index)}
                          sx={{ ml: 2 }}
                        >
                          <Delete />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>
          
          {/* Submit Button */}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1rem',
                bgcolor: '#FFE500',
                color: 'black',
                fontWeight: 600,
                ...buttonTransition,
                '&.Mui-disabled': {
                  bgcolor: '#f5f5f5',
                  color: '#bdbdbd'
                }
              }}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default UserProfile; 